import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AnaInboxWidget from "./AnaInboxWidget";
import DocBundleAlerts from "./DocBundleAlerts";
import {
  Users,
  CalendarDays,
  CalendarRange,
  BarChart3,
  Bell,
  Clock,
  MapPin,
  MessageCircle,
  GraduationCap,
  AlertTriangle,
  AlertCircle,
  Trophy,
  FileCheck,
  ClipboardList,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import useAppointmentActions from "@/hooks/useAppointmentActions";
import { formatDateES, todayISO, studentShortLabel, appointmentRouteLabel } from "@/lib/utils-app";
import { APPOINTMENT_TYPE_SHORT } from "@/lib/appointment-types";
import ExamResultDialog from "./ExamResultDialog";

// V1.39 · Dashboard operativo — segmentación por clases restantes.
// V1.39.2 fix (code review LOW) · Etiquetas alineadas con los cortes reales +
// nuevo bucket "25+" para que ningún alumno con >25 clases quede fuera.
const BUCKET_ORDER = ["zero", "1-2", "3-4", "5-10", "11-15", "16-20", "21-25", "26+"];
const BUCKET_META = {
  "zero":  { label: "Bono agotado", on: "bg-red-50 border-red-300 hover:border-red-400",             text: "text-red-700" },
  "1-2":   { label: "1-2 clases",   on: "bg-amber-50 border-amber-300 hover:border-amber-400",       text: "text-amber-700" },
  "3-4":   { label: "Menos de 5",   on: "bg-orange-50 border-orange-300 hover:border-orange-400",    text: "text-orange-700" },
  "5-10":  { label: "5-10 clases",  on: "bg-blue-50 border-blue-300 hover:border-blue-400",          text: "text-blue-700" },
  "11-15": { label: "11-15 clases", on: "bg-emerald-50 border-emerald-300 hover:border-emerald-400", text: "text-emerald-700" },
  "16-20": { label: "16-20 clases", on: "bg-emerald-50 border-emerald-300 hover:border-emerald-400", text: "text-emerald-700" },
  "21-25": { label: "21-25 clases", on: "bg-emerald-50 border-emerald-300 hover:border-emerald-400", text: "text-emerald-700" },
  "26+":   { label: "25+ clases",   on: "bg-emerald-50 border-emerald-300 hover:border-emerald-400", text: "text-emerald-700" },
};

function StatCard({ label, value, tone = "neutral", icon: Icon, testid, onClick }) {
  const tones = {
    neutral: "bg-white border-neutral-200",
    orange: "bg-orange-50 border-orange-200",
    red: "bg-red-50 border-red-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    blue: "bg-blue-50 border-blue-200",
  };
  const valueColor = {
    neutral: "text-neutral-900",
    orange: "text-orange-700",
    red: "text-red-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    blue: "text-blue-700",
  }[tone];
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      data-testid={testid}
      onClick={onClick}
      className={
        "rounded-2xl border p-4 flex items-center gap-3 text-left w-full " +
        tones[tone] +
        (onClick ? " hover:border-orange-300 hover:shadow-sm transition-colors cursor-pointer" : "")
      }
    >
      {Icon && (
        <div className={"h-10 w-10 shrink-0 rounded-xl flex items-center justify-center " + (tone === "neutral" ? "bg-neutral-100 text-neutral-700" : "bg-white/70 text-neutral-800")}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">{label}</div>
        <div className={"text-2xl font-bold leading-tight " + valueColor}>{value}</div>
      </div>
    </Comp>
  );
}

function SectionCard({ title, count, icon: Icon, tone = "neutral", empty, children, action, testid }) {
  const toneClass = {
    neutral: "border-neutral-200",
    red: "border-red-200",
    amber: "border-amber-200",
    orange: "border-orange-200",
    blue: "border-blue-200",
  }[tone];
  const iconWrap = {
    neutral: "bg-neutral-100 text-neutral-700",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-700",
    orange: "bg-orange-100 text-orange-700",
    blue: "bg-blue-100 text-blue-700",
  }[tone];
  return (
    <section data-testid={testid} className={"card-surface p-4 sm:p-5 border " + toneClass}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <span className={"h-8 w-8 rounded-lg flex items-center justify-center " + iconWrap}>
              <Icon className="h-4 w-4" />
            </span>
          )}
          <h3 className="font-bold text-base sm:text-lg leading-tight">{title}</h3>
          {typeof count === "number" && (
            <span className="ml-1 inline-flex items-center rounded-full bg-neutral-900 text-white text-[11px] font-semibold px-2 py-0.5">
              {count}
            </span>
          )}
        </div>
        {action}
      </div>
      {(!children || (Array.isArray(children) && children.length === 0)) && empty ? (
        <div className="text-sm text-neutral-500 py-2">{empty}</div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}

function TodayRow({ appt, onWhatsapp, onReminder, onOpenStudent }) {
  const typeLabel = APPOINTMENT_TYPE_SHORT[appt.appointment_type || "clase_practica"] || "Práctica";
  return (
    <div
      data-testid={`dash-today-${appt.id}`}
      className="rounded-xl border border-neutral-100 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 justify-between"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onOpenStudent?.(appt.student?.id)}
            className="font-semibold text-sm text-left hover:underline hover:text-orange-700"
            title="Abrir ficha del alumno"
            data-testid={`dash-today-student-${appt.id}`}
          >
            {appt.student?.name || "—"}
          </button>
          <span className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-semibold px-2 py-0.5">
            {typeLabel}
          </span>
          {appt.reminder_sent && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5">
              <Bell className="h-3 w-3" /> Recordado
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 flex flex-wrap items-center gap-3 mt-0.5">
          <span className="flex items-center gap-1 font-mono">
            <Clock className="h-3 w-3" /> {appt.time}
            {appt.end_time && <> – {appt.end_time}</>}
          </span>
          <span className="flex items-center gap-1 truncate" data-testid={`dash-today-route-${appt.id}`}>
            <MapPin className="h-3 w-3" />
            {appointmentRouteLabel(appt)}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`dash-today-whatsapp-${appt.id}`}
          onClick={() => onWhatsapp(appt)}
          className="btn-whatsapp !h-8 !px-3 !text-xs"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          WhatsApp
        </button>
        <button
          type="button"
          data-testid={`dash-today-reminder-${appt.id}`}
          onClick={() => onReminder(appt)}
          className="btn-secondary !h-8 !px-3 !text-xs"
        >
          <Bell className="h-3.5 w-3.5" />
          Recordar
        </button>
      </div>
    </div>
  );
}

function ReminderRow({ appt, onReminder, onOpenStudent }) {
  const hours = appt.hours_until;
  const urgent = hours != null && hours < 24;
  const dotClass = urgent ? "bg-red-500" : "bg-orange-500";
  return (
    <div
      data-testid={`dash-reminder-${appt.id}`}
      className="rounded-xl border border-neutral-100 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2 justify-between"
    >
      <div className="min-w-0 flex items-center gap-2">
        <span className={"h-2 w-2 rounded-full shrink-0 " + dotClass} />
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => onOpenStudent?.(appt.student?.id)}
            data-testid={`dash-reminder-student-${appt.id}`}
            className="font-semibold text-sm truncate text-left hover:underline hover:text-orange-700"
            title="Abrir ficha del alumno"
          >
            {appt.student?.name || "—"}
          </button>
          <div className="text-xs text-neutral-500 flex flex-wrap items-center gap-3 mt-0.5">
            <span className="font-mono">
              {formatDateES(appt.date)} · {appt.time}
            </span>
            {hours != null && (
              <span className={"font-semibold " + (urgent ? "text-red-700" : "text-orange-700")}>
                en {hours < 1 ? "<1" : Math.round(hours)}h
              </span>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid={`dash-reminder-send-${appt.id}`}
        onClick={() => onReminder(appt)}
        className="btn-whatsapp !h-8 !px-3 !text-xs"
      >
        <Bell className="h-3.5 w-3.5" /> Enviar
      </button>
    </div>
  );
}

function StudentBalanceRow({ student, tone, onClick }) {
  const badgeClass =
    tone === "red"
      ? "bg-red-100 text-red-800"
      : "bg-amber-100 text-amber-800";
  const label = student.remaining <= 0 ? "0" : student.remaining;
  return (
    <button
      type="button"
      data-testid={`dash-balance-${student.id}`}
      onClick={onClick}
      className="w-full text-left rounded-xl border border-neutral-100 px-3 py-2 flex items-center justify-between gap-2 hover:bg-orange-50 hover:border-orange-200 transition-colors"
      title="Ver ficha del alumno"
    >
      <span className="font-medium text-sm truncate" title={student.name}>{studentShortLabel(student) || student.name}</span>
      <span className={"inline-flex items-center gap-1 rounded-full text-[11px] font-semibold px-2 py-0.5 " + badgeClass}>
        <GraduationCap className="h-3 w-3" />
        {label} {student.remaining === 1 ? "clase" : "clases"}
      </span>
    </button>
  );
}

function ExamRow({ appt, onClick }) {
  const typeIcon = appt.appointment_type === "examen_practico" ? Trophy : FileCheck;
  const Icon = typeIcon;
  const typeLabel = APPOINTMENT_TYPE_SHORT[appt.appointment_type] || "Examen";
  return (
    <button
      type="button"
      data-testid={`dash-exam-${appt.id}`}
      onClick={onClick}
      className="w-full text-left rounded-xl border border-neutral-100 px-3 py-2 flex items-center gap-2 justify-between hover:bg-orange-50 hover:border-orange-200 transition-colors"
      title="Abrir ficha del alumno"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-orange-600 shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{appt.student?.name || "—"}</div>
          <div className="text-xs text-neutral-500">
            {formatDateES(appt.date)} · <span className="font-mono">{appt.time}</span>
          </div>
        </div>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-800 rounded-full px-2 py-0.5">
        {typeLabel}
      </span>
    </button>
  );
}

function PendingReviewCard({ item, onReview, onOpenStudent }) {
  const typeLabel = APPOINTMENT_TYPE_SHORT[item.appointment_type || "clase_practica"] || "Práctica";
  return (
    <div
      data-testid={`review-item-${item.id}`}
      className="rounded-xl border border-neutral-200 px-3 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onOpenStudent?.(item.student?.id)}
            className="font-semibold text-sm text-left hover:underline hover:text-orange-700"
            data-testid={`review-student-${item.id}`}
            title="Abrir ficha del alumno"
          >
            {item.student?.name || "—"}
          </button>
          <span className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-semibold px-2 py-0.5">
            {typeLabel}
          </span>
        </div>
        <div className="text-xs text-neutral-500 flex flex-wrap items-center gap-3 mt-0.5">
          <span className="font-mono">
            {formatDateES(item.date)} · {item.time}
            {item.end_time && ` – ${item.end_time}`}
          </span>
          <span className="flex items-center gap-1 truncate" data-testid={`review-route-${item.id}`}>
            <MapPin className="h-3 w-3" />
            {appointmentRouteLabel(item)}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`review-attended-${item.id}`}
          onClick={() => onReview(item, true)}
          className="btn-ghost border border-neutral-200 text-emerald-700 !h-9 !px-3 !text-xs"
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Asistió
        </button>
        <button
          type="button"
          data-testid={`review-missed-${item.id}`}
          onClick={() => onReview(item, false)}
          className="btn-ghost border border-neutral-200 text-red-700 !h-9 !px-3 !text-xs"
        >
          <XCircle className="h-3.5 w-3.5" /> Faltó
        </button>
      </div>
    </div>
  );
}

export default function DashboardTab({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingReview, setPendingReview] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showReviewList, setShowReviewList] = useState(false);
  const [referralAlerts, setReferralAlerts] = useState({ count: 0, items: [] });
  const [examDialog, setExamDialog] = useState({ open: false, appt: null });
  // V1.39 · Selección del bucket de clases restantes visible en el widget.
  const [activeBucket, setActiveBucket] = useState("zero");

  // V1.39 · Helpers de navegación (1 clic al detalle / listado filtrado).
  const openStudent = useCallback((studentId) => {
    if (!studentId) return;
    onNavigate?.("alumnos", { focusStudentId: studentId });
  }, [onNavigate]);
  const openBucket = useCallback((bucketKey) => {
    setActiveBucket(bucketKey);
    // Navegamos al listado de Alumnos con los IDs del bucket.
    const arr = (summary?.students_by_bucket?.[bucketKey] || []);
    const ids = arr.map((s) => s.id);
    if (ids.length === 0) return;
    onNavigate?.("alumnos", {
      studentIds: ids,
      label: BUCKET_META[bucketKey].label,
    });
  }, [onNavigate, summary]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingReview = useCallback(async () => {
    setReviewLoading(true);
    try {
      const { data } = await api.get("/appointments/pending-review");
      setPendingReview(data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Fire-and-forget: silent fetch of referral alerts for the dashboard chip
    api.get("/referrals/alerts")
      .then(({ data }) => setReferralAlerts(data || { count: 0, items: [] }))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (showReviewList) loadPendingReview();
  }, [showReviewList, loadPendingReview]);

  const actions = useAppointmentActions(load);

  const reviewItem = useCallback(async (item, attended) => {
    // Fase A · Si es examen, abrimos el diálogo de resultado (nunca review directo).
    const t = item.appointment_type;
    if (t === "examen_teorico" || t === "examen_practico") {
      setExamDialog({ open: true, appt: item });
      return;
    }
    try {
      await api.post(`/appointments/${item.id}/review`, { attended });
      toast.success(attended ? "Marcada como asistida" : "Marcada como faltó");
      await loadPendingReview();
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }, [load, loadPendingReview]);

  const kpis = useMemo(() => {
    if (!summary) return null;
    return {
      today: summary.today_appointments?.length || 0,
      pendingConfirm: summary.pending_confirmation || 0,
      pendingReminders: summary.pending_reminders?.count || 0,
      zero: summary.students_zero_classes?.length || 0,
      low: summary.students_low_classes?.length || 0,
      exams: summary.upcoming_exams?.length || 0,
      pendingReview: summary.pending_review_count || 0,
    };
  }, [summary]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">
            Resumen de {formatDateES(todayISO())}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="dash-refresh"
          className="btn-secondary !h-9 !px-3 !text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Actualizar
        </button>
      </div>

      {/* V1.56.1 · Alertas de packs de documentos completados */}
      <DocBundleAlerts />

      {/* V1.30.1 · Widget de Bandeja ANA compacto */}
      <AnaInboxWidget onNavigate={onNavigate} />

      {/* Quick access */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <button
          type="button"
          data-testid="dash-quick-alumnos"
          onClick={() => onNavigate?.("alumnos")}
          className="rounded-2xl border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-orange-300 transition-colors px-3 py-4 flex flex-col items-center gap-1 text-center"
        >
          <Users className="h-5 w-5 text-orange-600" />
          <span className="text-xs sm:text-sm font-semibold">Alumnos</span>
        </button>
        <button
          type="button"
          data-testid="dash-quick-calendario"
          onClick={() => onNavigate?.("calendario")}
          className="rounded-2xl border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-orange-300 transition-colors px-3 py-4 flex flex-col items-center gap-1 text-center"
        >
          <CalendarRange className="h-5 w-5 text-orange-600" />
          <span className="text-xs sm:text-sm font-semibold">Calendario</span>
        </button>
        <button
          type="button"
          data-testid="dash-quick-caja"
          onClick={() => onNavigate?.("reportes")}
          className="rounded-2xl border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-orange-300 transition-colors px-3 py-4 flex flex-col items-center gap-1 text-center"
        >
          <BarChart3 className="h-5 w-5 text-orange-600" />
          <span className="text-xs sm:text-sm font-semibold">Caja / Reportes</span>
        </button>
      </div>

      {/* Referral alerts chip */}
      {referralAlerts.count > 0 && (
        <button
          type="button"
          onClick={() => onNavigate?.("referidos")}
          data-testid="dash-referral-alerts"
          className="w-full flex items-center gap-3 rounded-2xl border-2 border-red-300 bg-red-50 hover:bg-red-100 px-4 py-3 text-left transition-colors"
        >
          <span className="text-2xl">🔔</span>
          <div className="flex-1">
            <div className="font-bold text-red-800 text-sm">
              {referralAlerts.count} referido{referralAlerts.count !== 1 ? "s" : ""} pendiente{referralAlerts.count !== 1 ? "s" : ""} de gestionar
            </div>
            <div className="text-[11px] text-red-700">
              Llevan más de 7 días sin avanzar de estado. Pulsa para revisar.
            </div>
          </div>
          <span className="text-red-700 font-bold">→</span>
        </button>
      )}

      {/* KPI strip — V1.39 · Todas las tarjetas son accionables (1 clic al listado filtrado). */}
      {loading && !kpis ? (
        <div className="text-neutral-500 text-sm">Cargando resumen…</div>
      ) : kpis ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          <StatCard
            testid="kpi-today" tone="orange" icon={CalendarDays}
            label="Citas hoy" value={kpis.today}
            onClick={() => onNavigate?.("calendario")}
          />
          <StatCard
            testid="kpi-pending-confirm" tone="amber" icon={AlertCircle}
            label="Sin confirmar" value={kpis.pendingConfirm}
            onClick={() => onNavigate?.("citas")}
          />
          <StatCard
            testid="kpi-reminders"
            tone={kpis.pendingReminders > 0 ? "red" : "neutral"}
            icon={Bell} label="Recordatorios <48h" value={kpis.pendingReminders}
            onClick={() => onNavigate?.("citas")}
          />
          <StatCard
            testid="kpi-zero"
            tone={kpis.zero > 0 ? "red" : "neutral"}
            icon={AlertTriangle} label="Bonos agotados" value={kpis.zero}
            onClick={() => openBucket("zero")}
          />
          <StatCard
            testid="kpi-low"
            tone={kpis.low > 0 ? "amber" : "neutral"}
            icon={GraduationCap} label="1-2 clases" value={kpis.low}
            onClick={() => openBucket("1-2")}
          />
          <StatCard
            testid="kpi-review"
            tone={kpis.pendingReview > 0 ? "blue" : "neutral"}
            icon={ClipboardList} label="Pdt. revisión" value={kpis.pendingReview}
            onClick={() => setShowReviewList(true)}
          />
        </div>
      ) : null}

      {summary && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Today */}
          <SectionCard
            testid="section-today"
            title="Citas de hoy"
            count={summary.today_appointments?.length || 0}
            icon={CalendarDays}
            tone="orange"
            empty="No hay citas para hoy."
          >
            {summary.today_appointments?.map((a) => (
              <TodayRow
                key={a.id}
                appt={a}
                onWhatsapp={actions.sendWhatsapp}
                onReminder={actions.sendReminder}
                onOpenStudent={openStudent}
              />
            ))}
          </SectionCard>

          {/* Reminders pending */}
          <SectionCard
            testid="section-reminders"
            title="Recordatorios pendientes"
            count={summary.pending_reminders?.count || 0}
            icon={Bell}
            tone={summary.pending_reminders?.count > 0 ? "red" : "neutral"}
            empty="Todos los recordatorios están enviados."
          >
            {summary.pending_reminders?.items?.map((a) => (
              <ReminderRow
                key={a.id}
                appt={a}
                onReminder={actions.sendReminder}
                onOpenStudent={openStudent}
              />
            ))}
          </SectionCard>

          {/* V1.39 · Segmentación completa por clases restantes */}
          <SectionCard
            testid="section-buckets"
            title="Clases restantes por alumno"
            icon={GraduationCap}
            tone="amber"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
              {BUCKET_ORDER.map((k) => {
                const arr = summary.students_by_bucket?.[k] || [];
                const meta = BUCKET_META[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => openBucket(k)}
                    data-testid={`dash-bucket-${k}`}
                    className={
                      "rounded-xl border p-2 flex flex-col items-start gap-0.5 text-left transition-colors hover:shadow-sm " +
                      (arr.length > 0 ? meta.on : "bg-neutral-50 border-neutral-200 hover:border-orange-300")
                    }
                    title="Ver alumnos de este rango"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                      {meta.label}
                    </span>
                    <span className={"text-2xl font-bold " + (arr.length > 0 ? meta.text : "text-neutral-500")}>
                      {arr.length}
                    </span>
                  </button>
                );
              })}
            </div>
            {(() => {
              const arr = summary.students_by_bucket?.[activeBucket] || [];
              if (arr.length === 0) {
                return (
                  <div className="text-xs text-neutral-500 italic py-1">
                    {BUCKET_META[activeBucket].label}: sin alumnos en este rango.
                  </div>
                );
              }
              return arr.slice(0, 8).map((s) => (
                <StudentBalanceRow
                  key={s.id}
                  student={s}
                  tone={activeBucket === "zero" ? "red" : "amber"}
                  onClick={() => openStudent(s.id)}
                />
              ));
            })()}
            {(summary.students_by_bucket?.[activeBucket] || []).length > 8 && (
              <button
                type="button"
                onClick={() => openBucket(activeBucket)}
                data-testid={`dash-bucket-more-${activeBucket}`}
                className="w-full mt-2 text-xs font-semibold text-orange-700 hover:text-orange-900 flex items-center justify-center gap-1"
              >
                Ver los {summary.students_by_bucket[activeBucket].length} alumnos
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </SectionCard>

          {/* Upcoming exams */}
          <SectionCard
            testid="section-exams"
            title="Próximos exámenes"
            count={summary.upcoming_exams?.length || 0}
            icon={Trophy}
            tone="blue"
            empty="No hay exámenes programados próximamente."
          >
            {summary.upcoming_exams?.map((a) => (
              <ExamRow key={a.id} appt={a} onClick={() => openStudent(a.student?.id)} />
            ))}
          </SectionCard>

          {/* Pending review — V1.39 · lista por alumno afectado + clicable */}
          <SectionCard
            testid="section-review"
            title="Pendientes de revisión"
            count={summary.pending_review_count || 0}
            icon={ClipboardList}
            tone={summary.pending_review_count > 0 ? "blue" : "neutral"}
            empty="No hay citas pendientes de revisar."
            action={
              summary.pending_review_count > 0 && (
                <button
                  type="button"
                  data-testid="review-toggle"
                  onClick={() => setShowReviewList((v) => !v)}
                  className="btn-secondary !h-8 !px-3 !text-xs"
                >
                  {showReviewList ? "Ocultar" : "Resolver"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )
            }
          >
            {/* Listado por alumno (clic → ficha) */}
            {(summary.pending_review_students || []).length > 0 && !showReviewList && (
              <div className="space-y-1.5">
                {summary.pending_review_students.slice(0, 8).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => openStudent(s.id)}
                    data-testid={`dash-review-student-${s.id}`}
                    className="w-full text-left rounded-xl border border-neutral-100 px-3 py-2 flex items-center justify-between gap-2 hover:bg-orange-50 hover:border-orange-200 transition-colors"
                  >
                    <span className="font-medium text-sm truncate">{s.name}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 text-[11px] font-semibold px-2 py-0.5">
                      <ClipboardList className="h-3 w-3" />
                      {s.count} pdte{s.count === 1 ? "" : "s"}
                    </span>
                  </button>
                ))}
                {summary.pending_review_students.length > 8 && (
                  <div className="text-[11px] text-neutral-500 text-center">
                    …y {summary.pending_review_students.length - 8} alumno(s) más
                  </div>
                )}
              </div>
            )}
            {showReviewList && (
              <div className="space-y-2">
                {reviewLoading ? (
                  <div className="text-sm text-neutral-500 py-2">Cargando…</div>
                ) : pendingReview.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-2">No hay pendientes de revisión.</div>
                ) : (
                  pendingReview.map((it) => (
                    <PendingReviewCard
                      key={it.id} item={it}
                      onReview={reviewItem}
                      onOpenStudent={openStudent}
                    />
                  ))
                )}
              </div>
            )}
          </SectionCard>

          {/* V1.39 · Documentos pendientes de validar (agrupados por profesor). */}
          <SectionCard
            testid="section-pending-docs"
            title="Documentos pendientes de validar"
            count={(summary.pending_docs_by_teacher || []).reduce((n, x) => n + x.count, 0)}
            icon={FileCheck}
            tone={(summary.pending_docs_by_teacher || []).length > 0 ? "amber" : "neutral"}
            empty="Ningún documento pendiente."
          >
            {(summary.pending_docs_by_teacher || []).map((t) => (
              <button
                key={t.teacher}
                type="button"
                onClick={() => onNavigate?.("alumnos", {
                  teacher: t.teacher === "—" ? "" : t.teacher,
                  docsFilter: "incomplete",
                  label: `Docs pdtes · ${t.teacher}`,
                })}
                data-testid={`dash-docs-teacher-${t.teacher}`}
                className="w-full text-left rounded-xl border border-neutral-100 px-3 py-2 flex items-center justify-between gap-2 hover:bg-orange-50 hover:border-orange-200 transition-colors"
                title="Ver alumnos con documentos pendientes"
              >
                <span className="font-semibold text-sm truncate">{t.teacher}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5">
                  <FileCheck className="h-3 w-3" />
                  {t.count} doc{t.count === 1 ? "" : "s"} · {t.students} alumno{t.students === 1 ? "" : "s"}
                </span>
              </button>
            ))}
          </SectionCard>
        </div>
      )}

      <ExamResultDialog
        open={examDialog.open}
        appointment={examDialog.appt}
        onOpenChange={(v) => setExamDialog((s) => ({ ...s, open: v }))}
        onSaved={async () => { await loadPendingReview(); await load(); }}
      />
    </div>
  );
}
